import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import type { Omittable } from "@salestrak/ui";

import {
  GetWorkspaceUsersDocument,
  TeamsListDataDocument,
  connectionRows,
  useMemberDirectory,
  type WorkspaceUserRow,
} from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { EntityData } from "#app/lib/entity-visuals";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { FILTER_MEMBER_STATUSES } from "#app/lib/role-utils";

export type DirectoryKind = "user" | "team";

/**
 * The members and teams the bootstrap pages do not carry, for the quick-filter
 * index: the workspace data holds one page of each (500 members, 100 teams),
 * so a value past it is looked up by id here.
 */
export type QuickFilterDirectory = Omittable<{
  /** The entity behind an id, or null while it is looked up or when it is gone. */
  entityOf: (id: string, kind: DirectoryKind) => EntityData | null;
  /** Whether the server has answered for an id and holds nothing for it —
   *  never true for an id still in flight. */
  missingOf: (id: string, kind: DirectoryKind) => boolean;
}>;

/**
 * A `QuickFilterDirectory` over the member directory (members, batched) and a
 * by-id read of `teamIds` the bootstrap team page lacks.
 */
export function useQuickFilterDirectory(
  teamIds: readonly string[],
): QuickFilterDirectory {
  const members = useMemberDirectory();
  const { teamMap } = useWorkspaceData();

  const unknownTeamsKey = useMemo(
    () => [...new Set(teamIds.filter((id) => !teamMap.has(id)))].sort().join(","),
    [teamIds, teamMap],
  );
  const variables = useMemo(() => {
    const ids = unknownTeamsKey ? unknownTeamsKey.split(",") : [];
    return { where: { teamIds: { hasSome: ids } }, limit: ids.length };
  }, [unknownTeamsKey]);
  const { data, previousData, loading } = useQuery(TeamsListDataDocument, {
    variables,
    skip: !unknownTeamsKey,
  });

  const fetchedTeams = useMemo(
    () =>
      new Map(
        ((data ?? previousData)?.teams?.edges ?? [])
          .filter((edge) => edge?.node)
          .map((edge) => [edge.node.teamId, edge.node]),
      ),
    [data, previousData],
  );
  const answeredTeams = useMemo(
    () =>
      data && !loading && unknownTeamsKey
        ? new Set(unknownTeamsKey.split(","))
        : new Set<string>(),
    [data, loading, unknownTeamsKey],
  );

  return useMemo<QuickFilterDirectory>(
    () => ({
      entityOf: (id, kind) => {
        if (kind === "team") {
          const team = teamMap.get(id) ?? fetchedTeams.get(id);
          return team ? { kind: "team", id, team } : null;
        }
        members.request([id]);
        const entity = members.entityOf(id);
        return entity?.kind === "user" ? entity : null;
      },
      missingOf: (id, kind) =>
        kind === "team"
          ? !teamMap.has(id) && !fetchedTeams.has(id) && answeredTeams.has(id)
          : members.missingOf(id),
    }),
    [members, teamMap, fetchedTeams, answeredTeams],
  );
}

const SEARCH_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 200;
const NO_ENTITIES: EntityData[] = [];

/**
 * Server matches for a typed query, for lists whose local options stop at
 * the bootstrap page: the members a filter offers (`FILTER_MEMBER_STATUSES`)
 * and teams. Each kind is asked only when `kinds` wants it; an empty
 * query asks nothing.
 */
export function useQuickSearchEntities(
  query: string,
  kinds: Omittable<{ members?: boolean; teams?: boolean }>,
): { members: EntityData[]; teams: EntityData[] } {
  const search = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);

  const memberVariables = useMemo(
    () => ({
      limit: SEARCH_LIMIT,
      skip: 0,
      where: { memberStatus: { in: [...FILTER_MEMBER_STATUSES] }, search },
    }),
    [search],
  );
  const memberResult = useQuery(GetWorkspaceUsersDocument, {
    variables: memberVariables,
    skip: !kinds.members || !search,
  });

  const teamVariables = useMemo(
    () => ({ where: { search }, limit: SEARCH_LIMIT }),
    [search],
  );
  const teamResult = useQuery(TeamsListDataDocument, {
    variables: teamVariables,
    skip: !kinds.teams || !search,
  });

  const memberData = kinds.members && search ? memberResult.data ?? memberResult.previousData : undefined;
  const teamData = kinds.teams && search ? teamResult.data ?? teamResult.previousData : undefined;

  const memberEntities = useMemo<EntityData[]>(
    () =>
      memberData
        ? connectionRows<WorkspaceUserRow>(memberData.users).map((row) => ({
            kind: "user",
            id: row.userId,
            user: row,
          }))
        : NO_ENTITIES,
    [memberData],
  );
  const teamEntities = useMemo<EntityData[]>(
    () =>
      teamData
        ? (teamData.teams?.edges ?? [])
            .filter((edge) => edge?.node)
            .map((edge) => ({ kind: "team", id: edge.node.teamId, team: edge.node }))
        : NO_ENTITIES,
    [teamData],
  );

  return useMemo(
    () => ({ members: memberEntities, teams: teamEntities }),
    [memberEntities, teamEntities],
  );
}
